import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Grid, Typography } from '@mui/material';
import { isSameDay, startOfDay } from 'date-fns';
import { uniqueId } from 'lodash';
import { Id, TimelineItemBase } from 'react-calendar-timeline';
import { EmployeeWithPrioritiesDto, PriorityItemDto } from '../../api';
import { dateToUnix } from '../../utils/Utils';
import { prioritiesToTimelineItems } from './TimelineMappers';
import { ITimelineOptions } from './TimelineOptions';

export interface EmployeeGroup {
  id: Id;
  title: string;
  parent: Id;
  root: boolean;
  currentUtilisation: number;
}

export type utilisationInfo = [Date, number];

/**
 * Generates a an id for a parent group
 * @param id employee id
 * @returns a parent group id
 */
export const groupParentId = (id: number) => `E${id}`;

/**
 * Checks if an id belongs to a parent group
 * @param id Id
 * @returns True if the id belongs to a parent group. Else, false
 */
export const isGroupParentId = (id: Id) => (id as string).charAt(0) === 'E';

/**
 * Extracts utilisation information from an employee's priorities and sorts it by date
 * @returns Utilisation information sorted by date
 */
const getSortedUtilisationInfo = (priorities: PriorityItemDto[]) => {
  const utilisationInfo: utilisationInfo[] = priorities.flatMap(x => [
    [startOfDay(new Date(x.startDate)), x.utilisation],
    [startOfDay(new Date(x.endDate)), -x.utilisation],
  ]);

  const sortedUtilisationInfo = utilisationInfo.sort((a, b) => a[0].getTime() - b[0].getTime());

  return sortedUtilisationInfo;
};

/**
 * Processes utilisation information in order to combine duplicate information into a single item.
 * Specifically, If there are duplicate dates, this method will combine all of them into a single
 *  UtilisationInfo item containing a sum of their total utitlisation values
 * @param utilisationInfo Utilisation information
 * @returns unique utilisation info
 */
const combineDuplicateUtilisationInfo = (utilisationInfo: utilisationInfo[]) => {
  return utilisationInfo.reduce((acc: utilisationInfo[], x) => {
    if (acc.length > 0) {
      const tail = acc[acc.length - 1];

      if (isSameDay(x[0], tail[0])) {
        tail[1] += x[1];
      } else {
        acc.push(x);
      }
    } else {
      acc.push(x);
    }
    return acc;
  }, []);
};

/**
 * Generates utilisation timeline items given an employee's utilisation information
 * @param employeeId employee Id
 * @param utilisationInfo employee's utilisation Info
 * @returns timeline items representing utilisation
 */
const generateUtilisationItems = (employeeId: number, utilisationInfo: utilisationInfo[]) => {
  return utilisationInfo.reduce((acc: TimelineItemBase<number>[], x) => {
    let sum = x[1];
    const start_time = dateToUnix(x[0]);

    if (acc.length > 0) {
      const prev = acc[acc.length - 1];
      prev.end_time = start_time;
      sum += prev.utilisation || 0;
    }

    acc.push({
      id: uniqueId('U'),
      group: groupParentId(employeeId),
      title: `${sum}%`,
      start_time,
      end_time: -1,
      utilisation: sum,
    } as TimelineItemBase<number>);

    return acc;
  }, []);
};

/**
 * Gets an employee's utilisation items to display on the timeline
 * @param employee employee
 * @param priorities priorities
 * @returns an employee's utilisation items to display on the timeline
 */
export const getUtilisationTimelineItems = (employee: EmployeeWithPrioritiesDto, priorities: PriorityItemDto[]) => {
  if (!employee.priorities) return [];

  const utilisationInfo = getSortedUtilisationInfo(priorities);
  const uniqueUtilisationInfo = combineDuplicateUtilisationInfo(utilisationInfo);
  const utilisationItems = generateUtilisationItems(employee.id, uniqueUtilisationInfo);

  return utilisationItems;
};

export const getPriorityTimelineItems = (priorities: PriorityItemDto[]) => {
  return prioritiesToTimelineItems(priorities);
};

/**
 * Method to toggle a parent group to expand/hide nested content
 * @param id Id of the toggled group
 * @param openGroups list of open groups
 * @param onToggle callback function to handle toggle
 */
const toggleGroup = (id: Id, openGroups: Id[], onToggle: (updatedOpenGroups: Id[]) => void) => {
  const index = openGroups.indexOf(id);
  let updatedOpenGroups = [...openGroups];

  if (index === -1) {
    updatedOpenGroups.push(id);
  } else {
    updatedOpenGroups.splice(index, 1);
  }

  onToggle(updatedOpenGroups);
};

/**
 * Defines group styling for parent and nested timeline groups.
 * @param group group
 * @param openGroups list of open groups
 * @param onToggle callback function to handle group toggle
 * @returns JSX element representing a group
 */
export const groupRenderer = (group: EmployeeGroup, openGroups: Id[], onToggle: (updatedOpenGroups: Id[]) => void) => {
  return Object.assign({}, group, {
    title: group.root ? (
      <Grid
        container
        direction="row"
        alignItems="center"
        justifyContent="center"
        onClick={() => {
          toggleGroup(group.id, openGroups, onToggle);
        }}
      >
        <Grid item whiteSpace={'normal'} xs={10}>
          <Typography fontWeight={500} lineHeight={1} textAlign="center" justifyContent="center" alignItems="center">
            {group.title}{' '}
            <Typography component="span" variant="subtitle2" lineHeight={1} color={'forestGreen'} fontWeight="500">
              {group.currentUtilisation}%
            </Typography>
          </Typography>
        </Grid>

        <Grid item xs={2} alignContent="center" justifyContent={'center'}>
          {openGroups.includes(group.id) ? <ExpandLess /> : <ExpandMore />}
        </Grid>
      </Grid>
    ) : null,
  });
};

/**
 * Gets filtered priorities in the form of timeline items
 * @param employee employee with priorities
 * @param filters active filters
 * @returns filtered priorities in the form of timeline items
 */
export const getFilteredPriorities = (employee: EmployeeWithPrioritiesDto, options: ITimelineOptions) => {
  if (!employee.priorities) return [];

  return employee.priorities
    .filter(x => x.status && options.filters.includes(x.status))
    .filter(x => (options.billable && x.project?.isBillable) || (options.nonBillable && !x.project?.isBillable));
};
